import { MutableRefObject, useRef } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { Colors } from '@revenuemarket/design';
import { Input } from 'antd';

import { GroupTreeItem } from '~apis/custom-label';
import { FlattenTreeResult } from '~components/ui/HorizontalTreeTable/Table';
import SearchSelectAllActions from '~components/ui/SearchSelectAllActions';
import { css } from '~libs/emotion';
import { CashFlowType } from '~models/cashFlow';

interface Props {
  treeRef?: React.RefObject<HTMLDivElement>;
  inputRef: MutableRefObject<any>;
  treeList?: FlattenTreeResult<GroupTreeItem>[];
  dropDownStyle?: React.CSSProperties;
  searchValue?: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveKey: React.Dispatch<React.SetStateAction<string[] | null>>;
  activeKey: string[] | null;
  onSelect?: (value: string | number, key: string[]) => void;
  onApply?: (item?: FlattenTreeResult<GroupTreeItem>, selctedKey?: string[]) => void;
  onClear?: () => void;
  applyLoading?: boolean;
  disableActionOnInactiveItem?: boolean;
  multiple: boolean;
  menu: React.ReactNode;
  cashFlowType?: CashFlowType;
  footerElement?: React.ReactNode;
  headerElement?: React.ReactNode;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  onSelectAll?: () => void;
  onClearAllSelect?: () => void;
}

const GroupTreeDropDown: React.FC<Props> = ({
  treeRef,
  inputRef,
  treeList = [],
  dropDownStyle,
  searchValue,
  setSearchValue,

  setShow,
  setActiveKey,
  activeKey,

  onClear,
  onApply,
  applyLoading,

  menu,
  multiple,

  footerElement,
  headerElement,
  onSelectAll,
  onClearAllSelect,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  return (
    <div ref={treeRef}>
      <Flex
        direction="column"
        gap={8}
        ref={dropdownRef}
        style={{
          padding: 8,
          backgroundColor: Colors.white,
          borderRadius: 12,
          minWidth: 360,
          boxShadow:
            '0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
          ...dropDownStyle,
        }}
        className={css`
          &&& .ant-dropdown-menu-item {
            &:focus-visible {
              outline: none;
            }
          }
        `}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setShow(false);
            onClear?.();
          }
          const isFirstItem =
            e.currentTarget.querySelector('[role="menu"] > li:first-child') === e.target;
          if (e.key === 'ArrowUp' && isFirstItem) {
            e.preventDefault();

            setTimeout(() => {
              inputRef?.current?.focus(); // 이벤트 이후 포커스 보장
            }, 0);
          }
        }}
      >
        {headerElement}

        <Input.Search
          ref={inputRef}
          placeholder="그룹 검색..."
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              if (searchValue) {
                searchRef.current?.querySelector('[role="menu"] > li:first-child')?.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    ...e,
                    view: window,
                  }),
                );
                return;
              }
              dropdownRef.current?.querySelector('[role="menu"]')?.dispatchEvent(
                new KeyboardEvent('keydown', {
                  ...e,
                  view: window,
                }),
              );
            }
          }}
        />

        {searchValue && multiple && (
          <SearchSelectAllActions
            onSelectAll={() => onSelectAll?.()}
            onClearAllSelect={() => onClearAllSelect?.()}
            hasSelectedItems={activeKey != null && activeKey.length > 0}
          />
        )}

        <div
          style={{
            height: 'calc(44vh - 100px)',
            overflowY: 'auto',
          }}
          ref={searchRef}
          className={css`
            & ul {
              border-radius: 0px !important;
            }

            &::-webkit-scrollbar-track {
              background-color: ${Colors.white};
            }
            &&& .ant-dropdown-menu {
              box-shadow: none;
              overflow: visible;
            }
          `}
        >
          {menu}
        </div>
        {footerElement ? (
          <div
            onClick={() => {
              setShow(false);
            }}
          >
            {footerElement}
          </div>
        ) : (
          <Flex gap={8} style={{ marginTop: 8 }}>
            {multiple && (
              <Button
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  setActiveKey([]);
                }}
                border
              >
                초기화
              </Button>
            )}
            <Button
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
              icon={
                applyLoading ? (
                  <LoadingOutlined
                    width={20}
                    height={20}
                    style={{
                      color: Colors.white,
                    }}
                  />
                ) : undefined
              }
              disable={applyLoading || !activeKey}
              onClick={() => {
                const seletctedTreeItem = treeList.find((node) => activeKey?.includes(node.key));

                onApply?.(seletctedTreeItem ?? undefined, activeKey ?? []);
                setShow(false);
              }}
              buttonType="primary"
            >
              적용
            </Button>
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default GroupTreeDropDown;
